import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AppContextType, UserProfile, AppSettings, Theme } from '@types/index';
import { defaultAppSettings } from '@config/index';

interface AppContextValue {
  context: AppContextType;
  setUser: (user: UserProfile | undefined) => void;
  setSettings: (settings: Partial<AppSettings>) => void;
  setTheme: (theme: Theme) => void;
  reset: () => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

const defaultTheme: Theme = {
  primary: '#2563eb',
  secondary: '#64748b',
  background: '#ffffff',
  text: '#1e293b',
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [context, setContext] = useState<AppContextType>({
    settings: defaultAppSettings,
    theme: defaultTheme,
  });

  const setUser = useCallback((user: UserProfile | undefined) => {
    setContext((prev) => ({
      ...prev,
      user,
    }));
  }, []);

  const setSettings = useCallback((settings: Partial<AppSettings>) => {
    setContext((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        ...settings,
      },
    }));
  }, []);

  const setTheme = useCallback((theme: Theme) => {
    setContext((prev) => ({
      ...prev,
      theme,
    }));
  }, []);

  const reset = useCallback(() => {
    setContext({
      settings: defaultAppSettings,
      theme: defaultTheme,
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        context,
        setUser,
        setSettings,
        setTheme,
        reset,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};
